import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { buildApiUrl } from '../../config/api.config';
import MarketingReport from '../MarketingReport';
import CCMarketingReport from './CCMarketingReport';

interface GeneratedReportData {
  id: number;
  name: string;
  type: string;
  data: any;
  summary: any;
  metadata: any;
  created_at: string;
  updated_at: string;
}

const GeneratedReportViewer: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const [reportData, setReportData] = useState<GeneratedReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReport = async () => {
      if (!reportId) {
        setError('Report ID is required');
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(buildApiUrl(`/v2/reports/direct/${reportId}`), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result = await response.json();
          setReportData(result.report);
        } else if (response.status === 404) {
          setError('Report not found');
        } else if (response.status === 401) {
          setError('Authentication required');
          navigate('/login');
        } else {
          const result = await response.json();
          setError(result.message || 'Failed to load report');
        }
      } catch (err) {
        setError(`Network error: ${err instanceof Error ? err.message : 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [reportId, navigate]);

  const handleDelete = async () => {
    if (!reportId || !reportData) return;

    if (!confirm(`Are you sure you want to delete "${reportData.name}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(buildApiUrl(`/v2/reports/direct/${reportId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        navigate('/admin/reports');
      } else {
        const result = await response.json();
        alert(result.message || 'Failed to delete report');
      }
    } catch (err) {
      alert(`Delete error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">⚠️ Error</div>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={() => navigate('/admin/reports')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Reports
          </button>
        </div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600">Report not found</p>
          <button
            onClick={() => navigate('/admin/reports')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Reports
          </button>
        </div>
      </div>
    );
  }

  const isCodeyReport = reportData.metadata?.calculation_type === 'cody' || 
                       reportData.metadata?.calculationType === 'cody' ||
                       reportData.name?.toLowerCase().includes('cody') ||
                       reportData.name?.toLowerCase().includes('cc');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/reports')}
                className="text-gray-600 hover:text-gray-900"
              >
                ← Back to Reports
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">{reportData.name}</h1>
                <p className="text-sm text-gray-500">
                  Generated on {new Date(reportData.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                reportData.type === 'sales' 
                  ? 'bg-blue-100 text-blue-800' 
                  : reportData.type === 'combined'
                  ? 'bg-purple-100 text-purple-800'
                  : 'bg-green-100 text-green-800'
              }`}>
                {reportData.type.toUpperCase()}
              </span>
              
              {isCodeyReport && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  CODY CLINE
                </span>
              )}
              
              <button
                onClick={handleDelete}
                className="px-3 py-1 text-red-600 hover:text-red-800 text-sm font-medium border border-red-300 rounded-md hover:bg-red-50"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Report Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {(reportData.type === 'sales' || reportData.type === 'combined') && (
          <>
            {isCodeyReport ? (
              <CCMarketingReport data={reportData.data} />
            ) : (
             <MarketingReport data={reportData.data} />
            )}
          </>
        )}
        
        {reportData.type === 'adspend' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Ad Spend Report</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {reportData.data.adSpendBySource?.map((source: any, index: number) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900">{source.source}</h4>
                  <p className="text-2xl font-bold text-blue-600">
                    ${source.spend.toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
            
            {reportData.summary && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-900">Total Ad Spend</h4>
                <p className="text-2xl font-bold text-blue-600">
                  ${reportData.summary.totalAdSpend?.toLocaleString() || 0}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneratedReportViewer;